import * as React from "react";
import { Bell, CircleHelp, Menu, Search } from "lucide-react";
import { Link } from "react-router";
import { Button } from "../ui/button.js";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "../ui/dropdown-menu.js";
import { EmptyState } from "../feedback/empty-state.js";
import { ThemeToggle } from "../../theme/theme-toggle.js";
import { UserMenu, type UserMenuProps } from "./user-menu.js";
import { searchShortcutLabel } from "../../lib/platform.js";

/** A search experience the app supplies (`07_UI_UX_SPEC.md` §19): the trigger's placeholder plus the dialog it opens. */
export interface HeaderSearch {
  placeholder: string;
  renderDialog: (props: { open: boolean; onOpenChange: (open: boolean) => void }) => React.ReactNode;
}

export interface HeaderProps {
  onOpenMobileNav: () => void;
  user: UserMenuProps;
  /** School/campus/academic-year `ScopeSelector`s, per `07_UI_UX_SPEC.md` §3. Optional — a
   * phase with nothing to scope yet (or a single-option context) can omit this entirely. */
  scopeSelectors?: React.ReactNode;
  /** Global search; omitted = no search trigger (e.g. portal accounts, which have nothing to search across). */
  search?: HeaderSearch;
  /** The notifications control (bell + menu). Omitted = a quiet bell that says nothing has arrived. */
  notifications?: React.ReactNode;
  /** In-app Help page (`17_UI_VISUAL_DESIGN_SYSTEM.md` §2.1 "help/support"); omitted = no Help button. */
  helpHref?: string;
}

export function Header({ onOpenMobileNav, user, scopeSelectors, search, notifications, helpHref }: HeaderProps) {
  const [searchOpen, setSearchOpen] = React.useState(false);
  const shortcutLabel = React.useMemo(() => searchShortcutLabel(), []);

  React.useEffect(() => {
    if (!search) return undefined;
    function handleKeyDown(event: KeyboardEvent) {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setSearchOpen(true);
      }
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [search]);

  return (
    <header className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-3 border-b border-border bg-surface px-4 sm:px-6">
      <Button variant="ghost" size="icon" className="md:hidden" aria-label="Open navigation" onClick={onOpenMobileNav}>
        <Menu />
      </Button>

      {scopeSelectors && <div className="hidden items-center gap-1.5 sm:flex">{scopeSelectors}</div>}

      {search && (
        <button
          type="button"
          onClick={() => setSearchOpen(true)}
          className="flex h-9 w-full min-w-0 max-w-sm items-center gap-2 rounded-[var(--radius-control)] border border-input bg-surface-muted px-3 text-sm text-muted-foreground transition-colors hover:bg-muted"
        >
          <Search className="size-4 shrink-0" />
          <span className="truncate">{search.placeholder}</span>
          <kbd className="ml-auto hidden shrink-0 rounded border border-border bg-surface px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground sm:inline">
            {shortcutLabel}
          </kbd>
        </button>
      )}

      <div className="ml-auto flex shrink-0 items-center gap-1">
        {notifications ?? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="Notifications">
                <Bell />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-80 p-2">
              <EmptyState icon={Bell} title="No notifications yet" description="Notifications addressed to you appear here." />
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        {helpHref && (
          <Button variant="ghost" size="icon" asChild>
            <Link to={helpHref} aria-label="Help">
              <CircleHelp />
            </Link>
          </Button>
        )}
        <ThemeToggle />
        <UserMenu {...user} />
      </div>

      {search?.renderDialog({ open: searchOpen, onOpenChange: setSearchOpen })}
    </header>
  );
}
